// Pure helpers for CharterisAdminStatusCard.vue — kept out of <script setup> so they
// can be unit-tested directly (same reason as pricingSourceHelpers.ts).
import type { CharterisAdminStatusEvent } from '../../api/client'

/** UK wall-clock rendering of a UTC ISO timestamp (Europe/London, DST-aware — same
 *  approach as japan/niyMarker.ts). Null/unparseable ⇒ 'none' (never throws). */
export function formatUkTime(iso: string | null | undefined): string {
  if (!iso) return 'none'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return 'none'
  const fmt = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London', day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  })
  return `${fmt.format(d)} UK`
}

/** Colour for the SI live-ticker switch tile: green when enabled, amber when the
 *  server itself doesn't know (Redis unreachable — "unknown" is a third state the
 *  Python consumer's own fail-open read doesn't have), grey when deliberately
 *  disabled by an operator (not a fault, so not amber/red). */
export function siSwitchColour(state: 'enabled' | 'disabled' | 'unknown' | string): string {
  if (state === 'enabled') return '#4caf50'
  if (state === 'unknown') return '#fbc02d'
  return '#888'
}

/** One line of text for a nullable {atUtc, message} event — 'none' when absent. */
export function eventLine(event: CharterisAdminStatusEvent | null | undefined): string {
  if (!event) return 'none'
  return `${event.message} — ${formatUkTime(event.atUtc)}`
}

/** 'none' for a null latestNav, else "<navPence>p (<date>)". */
export function latestNavLine(nav: { date: string; navPence: number } | null | undefined): string {
  if (!nav) return 'none'
  return `${nav.navPence}p (${nav.date})`
}
